import { createAction, handleActions } from "redux-actions";

import {
  checkEmptyObject,
  localStorage,
  notification
} from "../../services";
import { FeaturedStores } from "../../services/api";
import { LocalStorageKey } from "../../models";
import { navigateToLogin } from "../../history";
import {
  UserI,
  rootReducerInterface,
  storeInterface
} from "../../typings/interfaces";

const FEATURED_GET = "STORE_FEATURED_GET";
const FEATURED_LOADING = "STORE_FEATURED_LOADING";
const FEATURED_CLEAR = "STORE_FEATURED_CLEAR";
const FOLLOWED_TOGGLE = "STORE_FOLLOWED_TOGGLE";
const FOLLOWED_SYNC = "STORE_FOLLOWED_SYNC";

const initialState: storeInterface = {
  featured: { items: [], totalCount: 0, page: 1, isLoading: false },
  followed: { ids: [] }
};

// Track in-flight toggles to prevent double-clicks
const pendingToggles: string[] = [];

// ACTIONS — FEATURED

const mapFeaturedResponse = (data: any, fallbackPage: number) => ({
  items: (data && (data.items || data.data)) || [],
  totalCount: (data && (data.totalCount || data.count)) || 0,
  page: (data && (data.page || data.currentPage)) || fallbackPage
});

export const setFeaturedStoresLoading = createAction(
  FEATURED_LOADING,
  (isLoading: boolean) => isLoading
);

export const getFeaturedStores = createAction(
  FEATURED_GET,
  async (module?: string, category?: string) => {
    try {
      const data = await FeaturedStores.list(module, category, 1);
      if (!data) return null;
      return mapFeaturedResponse(data, 1);
    } catch (error) {
      return null;
    }
  }
);

export const clearFeaturedStores = createAction(FEATURED_CLEAR, () => null);

// ACTIONS — FOLLOWED

export const toggleFollowStore = createAction(
  FOLLOWED_TOGGLE,
  async (storeId: string, currentIds: string[], user: UserI) => {
    if (!storeId) return null;
    if (pendingToggles.indexOf(storeId) > -1) return null;

    // Not logged in — persist intent and redirect to login
    if (!checkEmptyObject(user)) {
      localStorage.setItem(LocalStorageKey.pendingFollowStore, storeId);
      navigateToLogin();
      return null;
    }

    pendingToggles.push(storeId);

    const isFollowed = currentIds.indexOf(storeId) > -1;
    const nextIds = isFollowed
      ? currentIds.filter(id => id !== storeId)
      : [...currentIds, storeId];

    try {
      if (isFollowed) {
        await FeaturedStores.unsubscribe(storeId);
        notification.show(
          "Više nećete primati e-mail obavijesti kada se pojave novi oglasi ovog oglašivača."
        );
      } else {
        await FeaturedStores.subscribe(storeId);
        notification.show(
          "Od sada ćete dobivati e-mail obavijest kada se pojave novi oglasi ovog trgovca."
        );
      }

      const idx = pendingToggles.indexOf(storeId);
      if (idx > -1) pendingToggles.splice(idx, 1);

      return { ids: nextIds };
    } catch (error) {
      const idx = pendingToggles.indexOf(storeId);
      if (idx > -1) pendingToggles.splice(idx, 1);
      notification.showError(
        "Nije uspjelo ažuriranje praćenja. Pokušajte ponovno."
      );
      return null;
    }
  }
);

// Seed followed store ids from user profile, and if a pending follow intent
// exists in localStorage (set before login redirect), complete it now.
export const syncFollowedStores = createAction(
  FOLLOWED_SYNC,
  async (user: UserI, callback?: Function) => {
    if (!checkEmptyObject(user)) {
      callback && callback(user);
      return { ids: [] };
    }

    const currentIds = (user.followedStores || []).map(s => s.storeId);
    const pendingStoreId = localStorage.getItem(
      LocalStorageKey.pendingFollowStore
    );

    if (!pendingStoreId || currentIds.indexOf(pendingStoreId) > -1) {
      if (pendingStoreId) {
        localStorage.removeItem(LocalStorageKey.pendingFollowStore);
      }
      callback && callback(user);
      return { ids: currentIds };
    }

    try {
      await FeaturedStores.subscribe(pendingStoreId);
      localStorage.removeItem(LocalStorageKey.pendingFollowStore);
      notification.show(
        "Od sada ćete dobivati e-mail obavijest kada se pojave novi oglasi ovog trgovca."
      );
      callback && callback(user);
      return { ids: [...currentIds, pendingStoreId] };
    } catch (error) {
      localStorage.removeItem(LocalStorageKey.pendingFollowStore);
      callback && callback(user);
      return { ids: currentIds };
    }
  }
);

// SELECTORS

export const selectFeaturedStores = (state: rootReducerInterface) =>
  (state.store && state.store.featured) || initialState.featured;

export const selectFollowedStoreIds = (state: rootReducerInterface) =>
  (state.store && state.store.followed && state.store.followed.ids) || [];

export const selectIsStoreFollowed = (
  state: rootReducerInterface,
  storeId?: string
) => {
  if (!storeId) return false;
  return selectFollowedStoreIds(state).indexOf(storeId) > -1;
};

// REDUCER

const setFollowedIds = (state: storeInterface, { payload }) => {
  if (!payload) return state;
  return { ...state, followed: { ids: payload.ids } };
};

export default handleActions(
  {
    [FEATURED_GET]: (state: storeInterface, { payload }) => {
      if (!payload) {
        return { ...state, featured: { ...state.featured, isLoading: false } };
      }
      return { ...state, featured: { ...payload, isLoading: false } };
    },
    [FEATURED_LOADING]: (state: storeInterface, { payload }) => ({
      ...state,
      featured: { ...state.featured, isLoading: Boolean(payload) }
    }),
    [FEATURED_CLEAR]: (state: storeInterface) => ({
      ...state,
      featured: initialState.featured
    }),
    [FOLLOWED_TOGGLE]: setFollowedIds,
    [FOLLOWED_SYNC]: setFollowedIds
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Store/store.ducks.ts